import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import {
  actions,
  DELETE_ORG_SAML_PROVIDER_RESULT,
  GET_ORG_SAML_PROVIDER_RESULT,
  GET_ORG_SAML_SERVICE_PROVIDER_RESULT,
  SET_ORG_SAML_PROVIDER_RESULT,
} from './actions';
import { notificationActions } from '../../../components/Notification/redux/actions';
import { PageHeader } from '../../../components/PageHeader';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../components/PageHeader/Button';
import { Switch2 } from '../../../components/Switch2/Switch2';
import Strings from '../../../Strings';
import './OrgSamlConfiguration.scss';

const EMPTY_CONFIG = {
  enabled: false,
  idp_metadata_url: '',
  allow_idp_initiated: false,
  sign_authn_requests: false,
};

const EMPTY_SERVICE_PROVIDER_DETAILS = {
  metadata_url: '',
  entity_id: '',
  acs_url: '',
};

function ReadOnlyCopyField({ label, value, testId, onCopy, copyLabel, copyTitle }) {
  const hasValue = !!value;

  return (
    <div className="org-saml-configuration__field">
      <label className="org-saml-configuration__label">
        {label}
      </label>
      <div className="org-saml-configuration__copy-field-row">
        <input
          type="text"
          value={value}
          readOnly
          data-testid={testId}
          className="org-saml-configuration__input org-saml-configuration__input--readonly"
          onFocus={event => event.target.select()}
        />
        <Button
          class={`${HEADER_BUTTON_DARK_BLUE} org-saml-configuration__copy-button`}
          onClick={() => onCopy(value)}
          title={copyLabel}
          ariaLabel={copyTitle}
          disabled={!hasValue}
          testid={`${testId}-copy`}
        />
      </div>
    </div>
  );
}

ReadOnlyCopyField.propTypes = {
  copyLabel: PropTypes.string.isRequired,
  copyTitle: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onCopy: PropTypes.func.isRequired,
  testId: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

function OrgSamlConfiguration(props) {
  const { getConfig, getServiceProviderDetails, saveConfig, deleteConfig, showNotification, organizationName } = props;

  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [providerExists, setProviderExists] = React.useState(false);
  const [config, setConfig] = React.useState(EMPTY_CONFIG);
  const [draftConfig, setDraftConfig] = React.useState(EMPTY_CONFIG);
  const [serviceProviderDetails, setServiceProviderDetails] = React.useState(EMPTY_SERVICE_PROVIDER_DETAILS);

  const orgPrefix = organizationName ? `${organizationName}: ` : '';
  const enableSave =
    JSON.stringify(config) !== JSON.stringify(draftConfig);

  const load = React.useCallback(() => {
    setIsLoading(true);
    Promise.all([getConfig(), getServiceProviderDetails()]).then(([configResp, spDetailsResp]) => {
      if (configResp.type === GET_ORG_SAML_PROVIDER_RESULT) {
        const nextConfig = {
          enabled: !!configResp.response.enabled,
          idp_metadata_url: configResp.response.idp_metadata_url || '',
          allow_idp_initiated: !!configResp.response.allow_idp_initiated,
          sign_authn_requests: !!configResp.response.sign_authn_requests,
        };
        setConfig(nextConfig);
        setDraftConfig(nextConfig);
        setProviderExists(true);
      } else if (configResp?.response?.status === 404) {
        setConfig(EMPTY_CONFIG);
        setDraftConfig(EMPTY_CONFIG);
        setProviderExists(false);
      } else {
        showNotification(Strings.errors.internalError, 5000);
      }

      if (spDetailsResp.type === GET_ORG_SAML_SERVICE_PROVIDER_RESULT) {
        setServiceProviderDetails({
          metadata_url: spDetailsResp.response.metadata_url || '',
          entity_id: spDetailsResp.response.entity_id || '',
          acs_url: spDetailsResp.response.acs_url || '',
        });
      } else {
        showNotification(Strings.errors.internalError, 5000);
      }

      setIsLoading(false);
    });
  }, [getConfig, getServiceProviderDetails, showNotification]);

  React.useEffect(() => {
    load();
  }, [load, props.organizationId]);

  const onToggle = (sw, enabled) => {
    const fieldName = sw.props.name;
    setDraftConfig(prev => ({
      ...prev,
      [fieldName]: enabled,
    }));
  };

  const onInputChange = event => {
    const { name, value } = event.target;
    setDraftConfig(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const copyValue = async value => {
    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      showNotification(Strings.copiedToClipboard, 3000);
    } catch {
      showNotification(Strings.errors.internalError, 5000);
    }
  };

  const onSave = () => {
    const payload = {
      enabled: draftConfig.enabled,
      idp_metadata_url: draftConfig.idp_metadata_url.trim(),
      allow_idp_initiated: draftConfig.allow_idp_initiated,
    };

    if (!payload.idp_metadata_url) {
      showNotification(Strings.samlMetadataUrlRequired, 5000);
      return;
    }

    setIsSaving(true);
    saveConfig(payload).then(resp => {
      if (resp.type === SET_ORG_SAML_PROVIDER_RESULT) {
        showNotification(Strings.success.dataSavedSuccessfully, 5000);
        setIsSaving(false);
        load();
      } else {
        showNotification(Strings.errors.internalError, 5000);
        setIsSaving(false);
      }
    });
  };

  const onDisable = () => {
    setIsSaving(true);
    deleteConfig().then(resp => {
      if (resp.type === DELETE_ORG_SAML_PROVIDER_RESULT) {
        showNotification(Strings.success.dataSavedSuccessfully, 5000);
        setConfig(EMPTY_CONFIG);
        setDraftConfig(EMPTY_CONFIG);
        setProviderExists(false);
      } else {
        showNotification(Strings.errors.internalError, 5000);
      }
      setIsSaving(false);
    });
  };

  return (
    <>
      <PageHeader left={orgPrefix + Strings.samlConfigurationTab} />
      <div className="org-saml-configuration">
        <div className="org-saml-configuration__group org-saml-configuration__group--service-provider">
          <ReadOnlyCopyField
            label={Strings.samlSpMetadataUrl}
            value={serviceProviderDetails.metadata_url}
            testId="saml-sp-metadata-url"
            onCopy={copyValue}
            copyLabel={Strings.copy}
            copyTitle={Strings.copyToClipboard}
          />

          <ReadOnlyCopyField
            label={Strings.samlSpEntityId}
            value={serviceProviderDetails.entity_id}
            testId="saml-sp-entity-id"
            onCopy={copyValue}
            copyLabel={Strings.copy}
            copyTitle={Strings.copyToClipboard}
          />

          <ReadOnlyCopyField
            label={Strings.samlSpAcsUrl}
            value={serviceProviderDetails.acs_url}
            testId="saml-sp-acs-url"
            onCopy={copyValue}
            copyLabel={Strings.copy}
            copyTitle={Strings.copyToClipboard}
          />
        </div>

        {!providerExists && !isLoading && (
          <div className="org-saml-configuration__empty-state">
            {Strings.samlNotConfiguredForOrganization}
          </div>
        )}
        <div className="org-saml-configuration__group">
          <div className="org-saml-configuration__field">
            <label className="org-saml-configuration__label">
              {Strings.enabled}
            </label>
            <Switch2 checked={draftConfig.enabled} name="enabled" onChange={onToggle} disabled={isLoading || isSaving} />
          </div>

          <div className="org-saml-configuration__field">
            <label className="org-saml-configuration__label">
              {Strings.samlMetadataUrl}
            </label>
            <input
              type="text"
              name="idp_metadata_url"
              value={draftConfig.idp_metadata_url}
              onChange={onInputChange}
              disabled={isLoading || isSaving}
              data-testid="saml-idp-metadata-url"
              placeholder={Strings.samlMetadataUrlPlaceholder}
              className="org-saml-configuration__input"
            />
          </div>

          <div className="org-saml-configuration__field">
            <label className="org-saml-configuration__label">
              {Strings.samlAllowIdpInitiated}
            </label>
            <Switch2
              checked={draftConfig.allow_idp_initiated}
              name="allow_idp_initiated"
              onChange={onToggle}
              disabled={isLoading || isSaving}
            />
          </div>

          <div className="org-saml-configuration__field">
            <label className="org-saml-configuration__label">
              {Strings.samlSignAuthnRequests}
            </label>
            <Switch2 checked={draftConfig.sign_authn_requests} name="sign_authn_requests" disabled />
          </div>
        </div>
      </div>
      <div className="buttons">
        <Button
          class={HEADER_BUTTON_DARK_BLUE}
          onClick={onSave}
          title={Strings.save}
          disabled={isLoading || isSaving || !enableSave}
        />
        {providerExists && (
          <Button
            class={HEADER_BUTTON_DARK_BLUE}
            onClick={onDisable}
            title={Strings.delete}
            disabled={isLoading || isSaving}
          />
        )}
      </div>
    </>
  );
}

OrgSamlConfiguration.propTypes = {
  deleteConfig: PropTypes.func.isRequired,
  getConfig: PropTypes.func.isRequired,
  getServiceProviderDetails: PropTypes.func.isRequired,
  organizationId: PropTypes.string,
  organizationName: PropTypes.string,
  saveConfig: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getConfig: () => dispatch(actions.getOrgSamlProvider(ownProps.organizationId)),
  getServiceProviderDetails: () => dispatch(actions.getOrgSamlServiceProvider(ownProps.organizationId)),
  saveConfig: configuration => dispatch(actions.setOrgSamlProvider(ownProps.organizationId, configuration)),
  deleteConfig: () => dispatch(actions.deleteOrgSamlProvider(ownProps.organizationId)),
  showNotification: (message, timeout) => dispatch(notificationActions.show(message, timeout)),
});

export default connect(null, mapDispatchToProps)(OrgSamlConfiguration);
